/* eslint-disable max-lines */
import { Variable } from '@omnea-digital/data-variables'
import { Operation } from '@omnea-digital/extops-schema'
import { TFunction } from 'i18next'
import { Comparison, ConditionConfig } from '@omnea-app/packages/universal/conditionality/schema'
import { FormConfig } from '@omnea-app/packages/universal/formBuilder/form'
import {
  AssigneeTypeEnum,
  FormTemplates,
  FormTypeEnum,
  RequestStepTaskTypeEnum,
} from '@omnea-app/packages/universal/hasura/graphql'
import { GlobalIntegration, globalIntegrationsMetadata } from '@omnea-app/packages/universal/integrations/globalIntegrations'
import { assertNever } from '@omnea-app/packages/universal/type-guards/assertNever'
import { propertyIsDefined } from '@omnea-app/packages/universal/type-guards/propertyIsDefined'
import { requestorAssignee } from '@omnea-app/requests/variableAssigneeTypes'
import { SupplierOnlineResourceTypeSchema, type SupplierOnlineResourceType } from '@omnea-app/suppliers/SupplierOnlineResource'
import {
  ExternalOperationHttpMapping,
  ExternalOperationInputMapping,
  ExternalOperationSftpMapping,
  PrimaryOutputConfig,
} from '../../stages/ExternalOperation/helpers/inputMapping/types'
import { ExternalOperationOutputMapping } from '../../stages/ExternalOperation/helpers/outputMapping/types'
import { PollingConfiguration } from '../../stages/ExternalOperation/helpers/polling/types'
import { OutputMapping } from '../../stages/shared/outputMapping/types'
import { TaskDistribution } from '../../stages/utils/step/assignee/taskDistribution/types/taskDistribution'
import { GraphNode } from '../Graph'
import {
  defaultAuctionNotificationMessage,
  defaultBudgetApprovalNotificationMessage,
  defaultCancelAgreementNotificationMessage,
  defaultCommitSupplierDataNotificationMessage,
  defaultCreateAgreementNotificationMessage,
  defaultCreateAssessmentNotificationMessage,
  defaultCreatePONotificationMessage,
  defaultEngagementBlockNotificationMessage,
  defaultNotifyOnlyNotificationMessage,
  defaultRfxNotificationMessage,
  defaultSupplierPortalNotificationMessage,
  defaultTaskNotificationMessage,
} from './defaultNotificationMessage'
import type { AgentConfig } from '../../stages/Approval'
import type { TransformationOutputField } from '../../stages/DataTransformation/types'
import type { WorkflowBlockValidationError } from '@omnea-app/config/validateWorkflowSchema'
import type { DynamicNotificationAttachment, FileNotificationAttachment } from '@omnea-app/notifications/notificationHandlerTypes'
import type { CustomIntegration } from '@omnea-app/packages/universal/extopsApiClient/integrationTypes'
import type { AssigneeEntity } from '@omnea-app/packages/universal/workflow/assigneeEntity'
import type { BlockTask, FillInFormTask, FormTask } from '@omnea-app/packages/universal/workflow/taskTypes'

export enum BlockTypeEnum {
  APPROVAL = 'APPROVAL',
  BUDGET_APPROVAL = 'BUDGET_APPROVAL',
  CHILD_REQUEST_START = 'CHILD_REQUEST_START',
  ENGAGEMENT = 'ENGAGEMENT',
  HTTP = 'HTTP',
  INTAKE = 'INTAKE',
  MILESTONE = 'MILESTONE',
  NOTIFICATION = 'NOTIFICATION',
  RENEWAL_ASSESSMENT = 'RENEWAL_ASSESSMENT',
  SUPPLIER_ONBOARDING = 'SUPPLIER_ONBOARDING',
  EXTERNAL_OPERATION = 'EXTERNAL_OPERATION',
  CREATE_RENEWAL = 'CREATE_RENEWAL',
  COMMIT_SUPPLIER_DATA = 'COMMIT_SUPPLIER_DATA',
  CREATE_AGREEMENT = 'CREATE_AGREEMENT',
  CANCEL_AGREEMENT = 'CANCEL_AGREEMENT',
  CANCEL_ASSESSMENTS = 'CANCEL_ASSESSMENTS',
  CREATE_PO = 'CREATE_PO',
  RFX = 'RFX',
  AUCTION = 'AUCTION',
  CREATE_ASSESSMENT = 'CREATE_ASSESSMENT',
  PRICE_BENCHMARK = 'PRICE_BENCHMARK',
  DATA_TRANSFORMATION = 'DATA_TRANSFORMATION',
  WAITING = 'WAITING',
  SUPPLIER_ONLINE_RESOURCES = 'SUPPLIER_ONLINE_RESOURCES',
  AI_FORM_COMPLETION = 'AI_FORM_COMPLETION',
  INHERENT_RISK_ASSESSMENT = 'INHERENT_RISK_ASSESSMENT',
}

export const NON_RENAMEABLE_BLOCK_TYPES: ReadonlySet<BlockTypeEnum> = new Set([
  BlockTypeEnum.MILESTONE,
  BlockTypeEnum.CHILD_REQUEST_START,
])

export type EngagementFormTemplate = {
  id: string
  config: FormConfig
  title: string
  type: FormTypeEnum
  description: string
}

type LabelOnlyBlockConfig = {
  isStartingBlock?: boolean
  payload: {
    label: string
    description?: string
  }
}

export const getBlockTasks = (block: GraphNode, engagementFormTemplates: EngagementFormTemplate[]) => {
  const payload = block.metadata.config.payload

  if (block.metadata.type === BlockTypeEnum.ENGAGEMENT) {
    return {
      blockLabel: payload.label,
      tasks: engagementFormTemplates.map<FillInFormTask>((form) => ({
        formTemplateId: form.id,
        type: RequestStepTaskTypeEnum.FILL_IN,
      })),
    }
  }

  if (propertyIsDefined(payload, 'tasks')) {
    return {
      canvasModelId: block.id,
      blockLabel: payload.label,
      tasks: payload.tasks,
    }
  }

  return undefined
}

export const blockIsFormTask = (blockTask: BlockTask): blockTask is FormTask => {
  if ('formTemplateId' in blockTask) {
    return true
  }
  return false
}

export interface AssigneeItem {
  id: number
  name: string
}

export type BlockConfigByType = {
  [BlockTypeEnum.APPROVAL]: ApprovalBlockConfig
  [BlockTypeEnum.ENGAGEMENT]: EngagementBlockConfig
  [BlockTypeEnum.INTAKE]: IntakeBlockConfig
  [BlockTypeEnum.NOTIFICATION]: NotificationBlockConfig
  [BlockTypeEnum.RENEWAL_ASSESSMENT]: RenewalAssessmentBlockConfig
  [BlockTypeEnum.SUPPLIER_ONBOARDING]: SupplierOnboardingBlockConfig
  [BlockTypeEnum.BUDGET_APPROVAL]: BudgetApprovalBlockConfig
  [BlockTypeEnum.HTTP]: HttpBlockConfig
  [BlockTypeEnum.EXTERNAL_OPERATION]: ExternalOperationBlockConfig
  [BlockTypeEnum.CREATE_RENEWAL]: CreateRenewalBlockConfig
  [BlockTypeEnum.COMMIT_SUPPLIER_DATA]: CommitSupplierDataBlockConfig
  [BlockTypeEnum.CREATE_AGREEMENT]: CreateAgreementBlockConfig
  [BlockTypeEnum.CANCEL_AGREEMENT]: CancelAgreementBlockConfig
  [BlockTypeEnum.CANCEL_ASSESSMENTS]: CancelAssessmentsBlockConfig
  [BlockTypeEnum.CREATE_PO]: CreatePOBlockConfig
  [BlockTypeEnum.RFX]: RfxBlockConfig
  [BlockTypeEnum.AUCTION]: AuctionBlockConfig
  [BlockTypeEnum.CREATE_ASSESSMENT]: CreateAssessmentBlockConfig
  [BlockTypeEnum.PRICE_BENCHMARK]: PriceBenchmarkBlockConfig
  [BlockTypeEnum.DATA_TRANSFORMATION]: DataTransformationBlockConfig
  [BlockTypeEnum.WAITING]: WaitingBlockConfig
  [BlockTypeEnum.SUPPLIER_ONLINE_RESOURCES]: SupplierOnlineResourcesBlockConfig
  [BlockTypeEnum.AI_FORM_COMPLETION]: AiFormCompletionBlockConfig
  [BlockTypeEnum.INHERENT_RISK_ASSESSMENT]: InherentRiskAssessmentBlockConfig
  [BlockTypeEnum.CHILD_REQUEST_START]: LabelOnlyBlockConfig
  // Blocks without configs
  [BlockTypeEnum.MILESTONE]: never
}

// Gets a config by type, if Type is unset, gets a union of all block configs
export type BlockConfig<Type extends BlockTypeEnum = BlockTypeEnum> = BlockConfigByType[Type]
interface BaseBlockConfig {
  isStartingBlock?: boolean
}

// A "linked" sub-workflow block mirrors a main-workflow block: it stores only this back-reference
// and no config of its own, so it can't drift from the block it mirrors. Its real BlockConfig is
// resolved from the linked main block at request creation (see resolveSubWorkflows).
export interface LinkedBlockConfig {
  payload: { linkedMainNodeId: string }
}

export const isLinkedBlockConfig = (config: BlockConfig | LinkedBlockConfig | undefined | null): config is LinkedBlockConfig =>
  config != null && typeof (config as LinkedBlockConfig).payload?.linkedMainNodeId === 'string'

interface Skip {
  skippedAt: string
  skippedBy: string
  comment?: string
}

export enum AssessmentType {
  EXTERNAL = 'EXTERNAL',
  INTERNAL = 'INTERNAL',
}

// DATA TRANSFORMATION
export type {
  TransformationValue,
  TransformationArg,
  TransformationRule,
  SwitchBranch,
  VariableReference,
  TransformationOutputField,
  TransformationOutputPrimitive,
} from '../../stages/DataTransformation/types'

export type DataTransformationBlockConfig = BaseBlockConfig & {
  payload: {
    label: string
    outputs: TransformationOutputField[]
    description?: string
    shouldMapOutputToForm?: boolean
    outputMapping?: OutputMapping
    tasks?: BlockTask[]
    assignees?: AssigneeEntity[]
    taskDistribution?: TaskDistribution
    skip?: Skip
  }
}

// WAITING
export type WaitingBlockConfig = BaseBlockConfig & {
  payload: {
    label: string
    description?: string
    condition: Comparison
  }
}

// INTAKE
export interface IntakeBlockConfig extends BaseBlockConfig {
  payload: {
    label: string
    tasks: BlockTask[]
    description?: string
    // Misnamed: actually used as the description text on the request-name field, not the input placeholder
    requestNamePlaceholder?: string
  }
}

// Renewal Assessment
export interface RenewalAssessmentBlockConfig extends BaseBlockConfig {
  payload: {
    label: string
    targetTimeValue: number | null
    assignees: AssigneeEntity[]
    tasks: BlockTask[]
    notificationMessage: NotificationMessage
    description?: string
    escalateToManager?: boolean
  }
}

// Supplier Onboarding
export interface SupplierOnboardingBlockConfig extends BaseBlockConfig {
  payload: {
    label: string
    targetTimeValue: number | null
    assignees?: AssigneeEntity[]
    hasStaticAssignees?: boolean
    taskDistribution?: TaskDistribution
    tasks: BlockTask[]
    notificationMessage: NotificationMessage
    emptyCommentsMessage?: string
    skip?: Skip
    description?: string
    prefillFromSupplierRecord: boolean
    prefillFromSupplierRecordOnSupplierProductMatch: boolean
    assessmentType: AssessmentType
    escalateToManager?: boolean
    shouldCompleteAutomatically?: boolean
  }
}

// Approval
export interface ApprovalBlockConfig extends BaseBlockConfig {
  payload: {
    label: string
    assignees: AssigneeEntity[]
    taskDistribution?: TaskDistribution
    targetTimeValue: number | null
    notificationMessage: NotificationMessage
    tasks?: BlockTask[]
    skip?: Skip
    description?: string
    prefillFromSupplierRecord: boolean
    prefillFromSupplierRecordOnSupplierProductMatch: boolean
    agentConfig?: AgentConfig
    escalateToManager?: boolean
    allowApproversToRejectRequest?: boolean
  }
}

export interface NotificationMessage {
  subject: string
  body: string
  attachments?: NotificationAttachment[]
  ccRecipients?: AssigneeEntity[]
}

export type NotificationAttachment = FileNotificationAttachment | DynamicNotificationAttachment

export { AssigneeEntitySchema } from '@omnea-app/packages/universal/workflow/assigneeEntity'
export type { AssigneeEntity } from '@omnea-app/packages/universal/workflow/assigneeEntity'

// These are variables we support for providing emails as assignees instead of users.
// that can then be used in the notification block to send notifications
export type EmailAssigneeVariable = 'supplierPortalAssignees' | 'primaryContactEmail'

export type {
  ReviewFormTask,
  FillInFormTask,
  EsignFillInTask,
  ExternalOperationTask,
  FormTask,
  EsignTask,
  SummaryApprovalTask,
  AccountingSupplierDataSyncTask,
  BlockTask,
} from '@omnea-app/packages/universal/workflow/taskTypes'
export type TaskForm = Pick<FormTemplates, 'title' | 'id' | 'type'>

export enum ApprovalTaskState {
  PENDING = 'PENDING',
  FAIL = 'REJECTED',
}

// Engagement
export interface EngagementBlockConfig extends BaseBlockConfig {
  payload: {
    label: string
    assignees: AssigneeEntity[]
    taskDistribution?: TaskDistribution
    targetTimeValue: number | null
    description?: string
    notificationMessage: NotificationMessage
  }
}

export enum EngagementTaskState {
  PENDING = 'PENDING',
  PASS = 'APPROVED',
  FAIL = 'REJECTED',
}

export const isBudgetApprovalNotificationMessage = (
  notificationMessage: NotificationMessage | BudgetApprovalNotificationMessage
): notificationMessage is BudgetApprovalNotificationMessage => {
  return 'approve' in notificationMessage && 'notify' in notificationMessage
}
// Budget Approval
export interface BudgetApprovalBlockConfig extends BaseBlockConfig {
  payload: {
    label: string
    targetTimeValue: number | null
    tasks: BlockTask[]
    notificationMessage: BudgetApprovalNotificationMessage
    budgetApprovalGroupId: string
    skipIfRequestor: boolean
    skipIfAlreadyApproved: boolean
    description?: string
    agentConfig?: AgentConfig
    prefillFromSupplierRecord?: boolean
    prefillFromSupplierRecordOnSupplierProductMatch?: boolean
    escalateToManager?: boolean
    allowApproversToRejectRequest?: boolean
  }
}

export interface BudgetApprovalNotificationMessage {
  approve: NotificationMessage
  notify: NotificationMessage
}

// Http block
export type HttpHeader = { header: string; value: string }
export interface HttpBlockConfig extends BaseBlockConfig {
  payload: {
    label: string
    authToken: string | null
    url: string
    headers: HttpHeader[]
    description?: string
  }
}

export interface CommitSupplierDataBlockConfig extends BaseBlockConfig {
  payload: {
    label: string
    assignees: AssigneeEntity[]
    taskDistribution?: TaskDistribution
    tasks: BlockTask[]
    targetTimeValue: number | null
    notificationMessage: NotificationMessage
    skip?: Skip
    description?: string
    syncWithAccountingIntegration?: boolean
    forceManualCompletion?: boolean
    escalateToManager?: boolean
  }
}

// Auction settings shared between the standalone Auction block and the RFx block's
// end-of-RFx auction (BUY-1176) — same fields, same builder editor in both places.
export type SharedAuctionSettings = {
  currency: string
  targetTimeValue: number | null
  showLotRank: boolean
  showLowestPricePerLot: boolean
  showStartingPrice: boolean
  notificationMessage: NotificationMessage
}

export const defaultSharedAuctionSettings: SharedAuctionSettings = {
  currency: 'GBP',
  targetTimeValue: null,
  showLotRank: false,
  showLowestPricePerLot: false,
  showStartingPrice: false,
  notificationMessage: defaultAuctionNotificationMessage,
}

export type RfxAuction = { isAllowed: false } | { isAllowed: true; settings: SharedAuctionSettings }

export interface RfxBlockConfig extends BaseBlockConfig {
  payload: {
    label: string
    assignees: AssigneeEntity[]
    taskDistribution?: TaskDistribution
    notificationMessage: NotificationMessage
    defaultFormTemplateIds: string[]
    targetTimeValue: number | null
    skip?: Skip
    description?: string
    requiresConfidentialityAgreement: boolean
    subWorkflowIds?: string[]
    // Behind the AUCTIONS flag: run an auction in place at the end of the RFx.
    auction?: RfxAuction
    // Behind the RFX_AUTONOMOUS_SOURCING flag: when true, the block auto-runs the autonomous setup
    // pipeline. Optional — older frozen configs predate it, and undefined reads as off.
    autonomousSourcing?: boolean
  }
}

// Auction Block — design-time config the workflow-builder sets (BUY-962).
// execute() only consumes label/assignees/currency at block entry; the duration
// and visibility-flag defaults are read later by the auctions service when lots
// are created (they live on auction_lots.settings, which has no row at SETUP).
export interface AuctionBlockConfig extends BaseBlockConfig {
  payload: SharedAuctionSettings & {
    label: string
    assignees: AssigneeEntity[]
    skip?: Skip
    description?: string
  }
}

// Create Assessment Block
export type TargetStartDateType = 'exact' | 'relative' | 'relativeToRequestCreatedAt'

export interface CreateAssessmentBlockConfig extends BaseBlockConfig {
  payload: {
    label: string
    targetTimeValue: number | null
    assignees: AssigneeEntity[]
    taskDistribution?: TaskDistribution
    notificationMessage: NotificationMessage
    shouldCompleteAutomatically: boolean
    tasks: FillInFormTask[]
    workflowId: string
    assessmentAssignees: AssigneeEntity[]
    frequency: string
    /** @deprecated Use targetStartDateType instead */
    shouldUseExactTargetStartDate?: boolean
    targetStartDateType?: TargetStartDateType
    exactTargetStartDate: string
    relativeTargetStartDate: string
    autoStartEnabled: boolean
    skip?: Skip
    description?: string
  }
}

export const resolveTargetStartDateType = (payload: CreateAssessmentBlockConfig['payload']): TargetStartDateType => {
  if (payload.targetStartDateType) return payload.targetStartDateType
  return payload.shouldUseExactTargetStartDate ? 'exact' : 'relative'
}

// AI Form Completion
export interface AiFormCompletionBlockConfig extends BaseBlockConfig {
  payload: {
    label: string
    assignees: AssigneeEntity[]
    targetTimeValue: number | null
    tasks: FillInFormTask[]
    additionalContext?: string
    description?: string
  }
}

// Price Benchmark
export interface PriceBenchmarkBlockConfig extends BaseBlockConfig {
  payload: {
    label: string
    assignees: AssigneeEntity[]
    targetTimeValue: number | null
    additionalContext?: string
    description?: string
    skip?: Skip
  }
}

// Inherent Risk Assessment
export interface RiskDimensionConfig {
  name: string
  label: string
  description: string
  weight: number
}

export interface InherentRiskAssessmentBlockConfig extends BaseBlockConfig {
  payload: {
    label: string
    description?: string
    formSources: string[]
    formInstructions?: string
    targetTimeValue: number | null
    dimensions?: RiskDimensionConfig[]
  }
}

export const DEFAULT_INHERENT_RISK_DIMENSIONS: RiskDimensionConfig[] = [
  {
    name: 'data_sensitivity',
    label: 'Data Sensitivity',
    description:
      'What types of data will the vendor access, store, or process on behalf of the organisation? Consider whether the data includes personally identifiable information (PII), financial records, health or medical data, confidential intellectual property, employee data, or sensitive commercial information. Assess whether the vendor will have access to production data or only anonymised/test data. Higher sensitivity data classifications (e.g. confidential, restricted) and broader data categories increase risk significantly.',
    weight: 0.25,
  },
  {
    name: 'system_access_level',
    label: 'System Access Level',
    description:
      'What level of access will the vendor require to internal systems, networks, or infrastructure? Consider whether access is read-only or includes write/modify/delete capabilities, whether they will access production environments or only lower environments, whether admin or privileged access is required, and whether they will connect to internal networks via VPN or direct integration. Vendors with broad, privileged, or unmonitored access represent a materially higher risk than those with narrow, scoped, or read-only access.',
    weight: 0.2,
  },
  {
    name: 'regulatory_scope',
    label: 'Regulatory Scope',
    description:
      'Is this engagement subject to regulatory, legal, or compliance requirements that impose obligations on how data is handled or how the vendor must operate? Consider frameworks such as GDPR, UK GDPR, HIPAA, PCI-DSS, SOC 2, ISO 27001, FCA regulations, or sector-specific rules. Also consider whether the engagement involves data subjects in regulated jurisdictions, or whether it touches systems or processes that are in scope for internal audit or external certification. Engagements with multiple overlapping regulatory obligations carry higher risk.',
    weight: 0.15,
  },
  {
    name: 'business_criticality',
    label: 'Business Criticality',
    description:
      'How critical is this vendor to the continuity of business operations? Consider whether the vendor provides a service that, if unavailable, would halt or severely degrade core business processes, revenue generation, or customer-facing services. Assess the availability of alternative vendors or internal fallback options, and how quickly the organisation could recover from a vendor failure. Vendors that are deeply embedded in critical workflows with no short-term substitute represent higher risk than discretionary or easily replaceable services.',
    weight: 0.15,
  },
  {
    name: 'data_volume',
    label: 'Data Volume',
    description:
      'How much data will the vendor handle, transfer, or process over the course of this engagement? Consider both the volume of records (e.g. number of individuals or transactions) and the frequency of data flows (e.g. real-time streaming vs. one-off batch transfer). Large-scale data processing amplifies the impact of any breach or misuse. Also consider whether the engagement involves bulk exports, data warehousing, or analytics over large datasets, as these increase exposure compared to narrow, record-level access.',
    weight: 0.1,
  },
  {
    name: 'engagement_type',
    label: 'Engagement Type',
    description:
      'What is the nature and structure of this vendor engagement? Consider whether it is a software-as-a-service (SaaS) product, a professional services arrangement, outsourced operations, a one-off project, or an embedded staffing model. SaaS and outsourced operations typically involve ongoing data processing obligations and higher inherent risk than time-limited projects. Also consider whether the vendor will sub-process data through third parties, and whether the engagement involves human access to data vs. automated processing only.',
    weight: 0.1,
  },
  {
    name: 'geographic_risk',
    label: 'Geographic Risk',
    description:
      "Where is the vendor headquartered, and where will data be stored or processed? Consider whether the vendor operates in jurisdictions with adequate data protection laws (e.g. EU adequacy decisions), or in countries flagged as high-risk by regulators or internal policy. Assess whether data will be transferred across borders, whether the vendor uses sub-processors in multiple regions, and whether there are geopolitical or sanctions-related risks associated with the vendor's country of operation. Vendors outside of recognised adequacy territories require additional safeguards.",
    weight: 0.05,
  },
]

// Supplier Online Resources
export { SupplierOnlineResourceTypeSchema, type SupplierOnlineResourceType }
export const SUPPLIER_ONLINE_RESOURCE_TYPES = SupplierOnlineResourceTypeSchema.options

export interface SupplierOnlineResourcesBlockConfig extends BaseBlockConfig {
  payload: {
    label: string
    description?: string
    assignees: AssigneeEntity[]
    targetTimeValue: number | null
    resourceTypes: SupplierOnlineResourceType[]
  }
}

// External Operation
export interface ExternalOperationBlockConfig extends BaseBlockConfig {
  payload: {
    subType: ExternalOperationSubType
    iconSrc?: string
    iconFilestoreId?: string
    customIntegrationId?: string
    isCustomAccountingIntegration?: boolean
    label: string
    operation: string // TODO exp-1389 consolidate operation and sybType
    integration: GlobalIntegration
    targetTimeValue: number | null
    shouldMapOutputToForm?: boolean
    assignees: AssigneeEntity[]
    taskDistribution?: TaskDistribution
    // TODO: Change this to just a single form template ID?
    // Should only be 1 task but keeping this in sync with the other blocks
    tasks?: BlockTask[]
    inputMapping?: ExternalOperationInputMapping
    httpMapping?: ExternalOperationHttpMapping
    sftpMapping?: ExternalOperationSftpMapping
    outputMapping?: ExternalOperationOutputMapping
    // May contain string
    operationConfig?: Record<string, string[]>
    input?: { variables: Variable[]; required: string[]; allowAdditionalVariables: boolean; customVariables?: Variable[] }
    output?: { variables: Variable[] }
    primaryOutputConfig?: PrimaryOutputConfig
    description?: string
    polling?: PollingConfiguration
    skip?: Skip
  }
}

export interface CreateAgreementBlockConfig extends BaseBlockConfig {
  payload: {
    label: string
    assignees: AssigneeEntity[]
    taskDistribution?: TaskDistribution
    tasks: BlockTask[]
    targetTimeValue: number | null
    notificationMessage: NotificationMessage
    shouldCompleteAutomatically: boolean
    skip?: Skip
    description?: string
    // Absent = copy. Override which tag sources feed the prefilled `tags` question.
    copyRequestTags?: boolean
    copyAutoGeneratedTags?: boolean
    copyLinkedAgreementTags?: boolean
  }
}

export interface CreatePOBlockConfig extends BaseBlockConfig {
  payload: {
    label: string
    assignees: AssigneeEntity[]
    taskDistribution?: TaskDistribution
    tasks: BlockTask[]
    targetTimeValue: number | null
    notificationMessage: NotificationMessage
    skip?: Skip
    description?: string
    forceManualCompletion?: boolean
    escalateToManager?: boolean
  }
}

export interface CancelAgreementBlockConfig extends BaseBlockConfig {
  payload: {
    label: string
    assignees: AssigneeEntity[]
    taskDistribution?: TaskDistribution
    tasks: BlockTask[]
    targetTimeValue: number | null
    notificationMessage: NotificationMessage
    shouldCompleteAutomatically: boolean
    skip?: Skip
    description?: string
  }
}

export enum CancelAssessmentsMode {
  All = 'ALL',
  Selective = 'SELECTIVE',
}

export type CancelAssessmentsBlockConfig = BaseBlockConfig & {
  payload: {
    label: string
    description?: string
    skip?: Skip
    cancellationMode?: CancelAssessmentsMode
    assignees?: AssigneeEntity[]
    taskDistribution?: TaskDistribution
    targetTimeValue: number | null
  }
}

export const resolveCancellationMode = (payload: BlockConfig['payload']): CancelAssessmentsMode =>
  'cancellationMode' in payload ? (payload.cancellationMode ?? CancelAssessmentsMode.All) : CancelAssessmentsMode.All

export const isSelectiveCancellation = (payload: BlockConfig['payload']): boolean =>
  resolveCancellationMode(payload) === CancelAssessmentsMode.Selective

const getExternalOperationBlockConfig = (node: ExternalOperationNode) => ({
  type: BlockTypeEnum.EXTERNAL_OPERATION,
  validationErrors: [],
  config: {
    payload: {
      subType: node.subType,
      label:
        globalIntegrationsMetadata[node.integration].blockLabel ??
        `${globalIntegrationsMetadata[node.integration].title} - ${node.operationName}`,
      iconSrc: globalIntegrationsMetadata[node.integration].logo,
      isCustomAccountingIntegration: node.integration === 'albertsons', // only non-custom integration supporting line item custom fields
      operation: node.operation,
      integration: node.integration,
      targetTimeValue: null,
      assignees: [],
    },
  } satisfies ExternalOperationBlockConfig,
})

const getCustomExternalOperationBlockConfig = (customIntegration: CustomIntegration) => {
  const basePayload = {
    label: `${customIntegration.name}`,
    customIntegrationId: customIntegration.customIntegrationId,
    iconFilestoreId: customIntegration.logo,
    targetTimeValue: null,
    assignees: [],
  }
  switch (customIntegration.type) {
    case 'http':
      return {
        type: BlockTypeEnum.EXTERNAL_OPERATION,
        validationErrors: [],
        config: {
          payload: {
            subType: 'HTTP-CUSTOM-INTEGRATION',
            isCustomAccountingIntegration: customIntegration.isAccountingIntegration ?? false,
            operation: 'http-custom-integration',
            integration: 'http',
            ...basePayload,
          },
        } satisfies ExternalOperationBlockConfig,
      }
    case 'sftp':
      return {
        type: BlockTypeEnum.EXTERNAL_OPERATION,
        validationErrors: [],
        config: {
          payload: {
            subType: 'SFTP-UPLOAD-FILE',
            operation: 'sftp-upload-file',
            integration: 'sftp',
            ...basePayload,
          },
        } satisfies ExternalOperationBlockConfig,
      }
    default:
      assertNever(customIntegration)
  }
}

export type ExternalOperationSubType = [
  'ALBERTSONS-CONVERT-TO-CSV',
  'IRONCLAD-COMPLETE-WORKFLOW',
  'IRONCLAD-INITIATE-WORKFLOW',
  'IRONCLAD-WAIT-FOR-COMPLETION',
  'IRONCLAD-UPDATE-WORKFLOW-APPROVAL',
  'IRONCLAD-GET-DOCUMENT',
  'JIRA-CREATE-TICKET',
  'JIRA-SERVICE-MANAGEMENT-CREATE-TICKET',
  'CREDITSAFE-GENERATE_REPORT',
  'DOCUSIGN-DOCUMENT-UPLOAD',
  'DOCUSIGN-CLM-UPLOAD-DOCUMENT',
  'DOCUSIGN-ESIGN-CREATE-ENVELOPE',
  'PROCESS-UNITY-IMPORT-EXPORT',
  'TPRM-CONTINUOUS-MONITORING-POINT-IN-TIME-CHECK',
  'PANDADOC-CREATE-AGREEMENT',
  'ADOBE-SIGN-CREATE-AGREEMENT',
  'MESH-CREATE-VIRTUAL-CARD',
  'NOOP-TEST',
  'ONE-TRUST-CREATE-ASSESSMENT',
  'ONE-TRUST-CREATE-VENDOR',
  'ZAPIER-ZAP',
  'ZENDESK-CREATE-TICKET',
  'HTTP-CUSTOM-INTEGRATION',
  'THOMSON-REUTERS-GENERATE-REPORT',
  'SFTP-UPLOAD-FILE',
  'COUPA-CLM-CREATE-CONTRACT',
  'CONTRACTPODAI-CREATE-CONTRACT',
  'TRULIOO-TRIGGER-WORKFLOW',
  'GOOGLE-DRIVE-UPLOAD-FILE',
  'LINKSQUARES-CREATE-AGREEMENT',
  'LINKSQUARES-APPROVE-TASK',
  'LINKSQUARES-PULL-COMPLETED-AGREEMENT',
  'LINKSQUARES-CREATE-AND-PULL-AGREEMENT',
  'CONGA-CONTRACTS-CREATE-CONTRACT',
  'PUNCHOUT-GENERIC-CREATE-ORDER-REQUEST',
  'PUNCHOUT-AMAZON-BUSINESS-CREATE-ORDER-REQUEST',
  'PUNCHOUT-UNITE-CREATE-ORDER-REQUEST',
  'PUNCHOUT-CDW-CREATE-ORDER-REQUEST',
  'TROPIC-PRICE-BENCHMARK',
][number]

// TODO EXP-1388 load operations from extops service instead of hardcoding them
export const externalOperations: Record<ExternalOperationSubType, ExternalOperationNode> = {
  'ALBERTSONS-CONVERT-TO-CSV': {
    integration: 'albertsons',
    subType: 'ALBERTSONS-CONVERT-TO-CSV',
    operationName: 'Convert to CSV',
    operation: 'albertsons-convert-to-csv',
    description: 'Convert request data to CSV',
  },
  'ADOBE-SIGN-CREATE-AGREEMENT': {
    integration: 'adobe-sign',
    subType: 'ADOBE-SIGN-CREATE-AGREEMENT',
    operationName: 'Create Agreement (eSign)',
    operation: 'adobe-sign-create-agreement',
    description: 'Create an agreement in Adobe Sign and wait for it to be signed',
  },

  'CREDITSAFE-GENERATE_REPORT': {
    integration: 'creditsafe',
    subType: 'CREDITSAFE-GENERATE_REPORT',
    operationName: 'Generate Report',
    operation: 'creditsafe-generate-report',
    description: 'Create a credit report in Credit Safe',
  },
  'DOCUSIGN-DOCUMENT-UPLOAD': {
    integration: 'docusign',
    subType: 'DOCUSIGN-DOCUMENT-UPLOAD',
    operationName: 'Document Upload (CLM)',
    operation: 'docusign-upload-document',
    description: 'Upload a document to DocuSign and wait for it to be signed',
  },
  'DOCUSIGN-CLM-UPLOAD-DOCUMENT': {
    integration: 'docusign-clm',
    subType: 'DOCUSIGN-CLM-UPLOAD-DOCUMENT',
    operationName: 'Upload Document',
    operation: 'docusign-clm-upload-document',
    description:
      'Upload a document to DocuSign CLM and either pull the uploaded document immediately or await external status before pulling',
  },
  'DOCUSIGN-ESIGN-CREATE-ENVELOPE': {
    integration: 'docusign',
    subType: 'DOCUSIGN-ESIGN-CREATE-ENVELOPE',
    operationName: 'Create Envelope (eSign)',
    operation: 'docusign-esign-create-envelope',
    description: 'Create an envelope with a contract in DocuSign eSign and wait for it to be signed',
  },
  'IRONCLAD-COMPLETE-WORKFLOW': {
    integration: 'ironclad',
    subType: 'IRONCLAD-COMPLETE-WORKFLOW',
    operationName: 'Run Workflow End-to-End',
    operation: 'ironclad-complete-workflow',
    description: 'Create an Ironclad workflow and wait for it to complete',
  },
  'IRONCLAD-INITIATE-WORKFLOW': {
    integration: 'ironclad',
    subType: 'IRONCLAD-INITIATE-WORKFLOW',
    operationName: 'Initiate Workflow',
    operation: 'ironclad-initiate-workflow',
    description: 'Initiate an Ironclad workflow',
  },
  'IRONCLAD-WAIT-FOR-COMPLETION': {
    integration: 'ironclad',
    subType: 'IRONCLAD-WAIT-FOR-COMPLETION',
    operationName: 'Wait for Completion',
    operation: 'ironclad-wait-for-completion',
    description: 'Wait for an Ironclad workflow to complete',
  },
  'IRONCLAD-UPDATE-WORKFLOW-APPROVAL': {
    integration: 'ironclad',
    subType: 'IRONCLAD-UPDATE-WORKFLOW-APPROVAL',
    operationName: 'Update Workflow Approval',
    operation: 'ironclad-update-workflow-approval',
    description: 'Update a workflow approval in Ironclad',
  },
  'IRONCLAD-GET-DOCUMENT': {
    integration: 'ironclad',
    subType: 'IRONCLAD-GET-DOCUMENT',
    operationName: 'Get Document',
    operation: 'ironclad-get-document',
    description: 'Get a document from Ironclad',
  },
  'PROCESS-UNITY-IMPORT-EXPORT': {
    integration: 'process-unity',
    subType: 'PROCESS-UNITY-IMPORT-EXPORT',
    operationName: 'Import/Export',
    operation: 'process-unity-import-export',
    description: 'Import an assessment into Process Unity and wait for a report to be completed',
  },
  'TPRM-CONTINUOUS-MONITORING-POINT-IN-TIME-CHECK': {
    integration: 'tprm-continuous-monitoring',
    subType: 'TPRM-CONTINUOUS-MONITORING-POINT-IN-TIME-CHECK',
    operationName: 'Risk Screening',
    operation: 'tprm-continuous-monitoring-point-in-time-check',
    description: 'Scan suppliers for third party risk as you onboard them',
  },
  'THOMSON-REUTERS-GENERATE-REPORT': {
    integration: 'thomson-reuters',
    subType: 'THOMSON-REUTERS-GENERATE-REPORT',
    operationName: 'Generate Report',
    operation: 'thomson-reuters-generate-report',
    description: 'Create a risk report in Thomson Reuters',
  },
  'JIRA-CREATE-TICKET': {
    integration: 'jira',
    subType: 'JIRA-CREATE-TICKET',
    operationName: 'Create Ticket',
    operation: 'jira-create-ticket',
    description: 'Create a ticket in Jira and wait for it to complete',
  },
  'JIRA-SERVICE-MANAGEMENT-CREATE-TICKET': {
    integration: 'jira-service-management',
    subType: 'JIRA-SERVICE-MANAGEMENT-CREATE-TICKET',
    operationName: 'Create Ticket',
    operation: 'jira-service-management-create-ticket',
    description: 'Create a ticket in Jira Service Management and wait for it to complete',
  },
  'MESH-CREATE-VIRTUAL-CARD': {
    integration: 'mesh',
    subType: 'MESH-CREATE-VIRTUAL-CARD',
    operationName: 'Create Virtual Card',
    operation: 'mesh-create-virtual-card',
    description: 'Create a virtual card in Mesh Payments',
  },
  'NOOP-TEST': {
    integration: 'noop',
    subType: 'NOOP-TEST',
    operationName: 'Test',
    operation: 'noop',
    description: 'No operation (for testing purposes)',
  },
  'ONE-TRUST-CREATE-ASSESSMENT': {
    integration: 'onetrust',
    subType: 'ONE-TRUST-CREATE-ASSESSMENT',
    operationName: 'Create Assessment',
    operation: 'onetrust-create-assessment',
    description: 'Create a One Trust assessment',
  },
  'ONE-TRUST-CREATE-VENDOR': {
    integration: 'onetrust',
    subType: 'ONE-TRUST-CREATE-VENDOR',
    operationName: 'Create Vendor',
    operation: 'onetrust-create-vendor',
    description: 'Create a One Trust vendor',
  },
  'PANDADOC-CREATE-AGREEMENT': {
    integration: 'pandadoc',
    subType: 'PANDADOC-CREATE-AGREEMENT',
    operationName: 'Create Agreement',
    operation: 'pandadoc-create-agreement',
    description: 'Create an agreement in PandaDoc and wait for it to be signed',
  },
  'ZAPIER-ZAP': {
    integration: 'zapier',
    subType: 'ZAPIER-ZAP',
    operationName: 'Trigger Integration',
    operation: 'zapier-execute-zap',
    description: 'Execute an integration via a Zapier zap',
  },
  'ZENDESK-CREATE-TICKET': {
    integration: 'zendesk',
    subType: 'ZENDESK-CREATE-TICKET',
    operationName: 'Create Ticket',
    operation: 'zendesk-create-ticket',
    description: 'Create a ticket in Zendesk',
  },
  'COUPA-CLM-CREATE-CONTRACT': {
    integration: 'coupa-clm',
    subType: 'COUPA-CLM-CREATE-CONTRACT',
    operationName: 'Create Contract',
    operation: 'coupa-clm-contract',
    description: 'Create a contract and upload metadata to Coupa CLM',
  },
  'TRULIOO-TRIGGER-WORKFLOW': {
    integration: 'trulioo',
    subType: 'TRULIOO-TRIGGER-WORKFLOW',
    operationName: 'Trigger workflow',
    operation: 'trulioo-trigger-workflow',
    description: 'Trigger a Trulioo workflow',
  },
  'HTTP-CUSTOM-INTEGRATION': {
    integration: 'http',
    subType: 'HTTP-CUSTOM-INTEGRATION',
    operationName: 'HTTP Custom Integration',
    operation: 'http-custom-integration',
    description: 'Execute a custom HTTP integration',
  },
  'SFTP-UPLOAD-FILE': {
    integration: 'sftp',
    subType: 'SFTP-UPLOAD-FILE',
    operationName: 'Upload File via SFTP',
    operation: 'sftp-upload-file',
    description: 'Upload a file to an SFTP server',
  },
  'CONTRACTPODAI-CREATE-CONTRACT': {
    integration: 'contractpodai',
    subType: 'CONTRACTPODAI-CREATE-CONTRACT',
    operationName: 'Create Contract',
    operation: 'contractpodai-create-contract',
    description: 'Create a contract in ContractPodAI',
  },
  'GOOGLE-DRIVE-UPLOAD-FILE': {
    integration: 'google-drive',
    subType: 'GOOGLE-DRIVE-UPLOAD-FILE',
    operationName: 'Upload File',
    operation: 'google-drive-upload-file',
    description: 'Upload a file to Google Drive',
  },
  'LINKSQUARES-CREATE-AGREEMENT': {
    integration: 'linksquares',
    subType: 'LINKSQUARES-CREATE-AGREEMENT',
    operationName: 'Create Agreement',
    operation: 'linksquares-create-agreement',
    description: 'Create an agreement in LinkSquares',
  },
  'LINKSQUARES-APPROVE-TASK': {
    integration: 'linksquares',
    subType: 'LINKSQUARES-APPROVE-TASK',
    operationName: 'Approve Task',
    operation: 'linksquares-approve-task',
    description: 'Approve an agreement task in LinkSquares',
  },
  'LINKSQUARES-PULL-COMPLETED-AGREEMENT': {
    integration: 'linksquares',
    subType: 'LINKSQUARES-PULL-COMPLETED-AGREEMENT',
    operationName: 'Pull Completed Agreement',
    operation: 'linksquares-pull-completed-agreement',
    description: 'Pull a completed agreement from LinkSquares',
  },
  'LINKSQUARES-CREATE-AND-PULL-AGREEMENT': {
    integration: 'linksquares',
    subType: 'LINKSQUARES-CREATE-AND-PULL-AGREEMENT',
    operationName: 'Create and Pull Agreement',
    operation: 'linksquares-create-and-pull-agreement',
    description: 'Create an agreement, wait for it to be signed and pull it from LinkSquares',
  },
  'CONGA-CONTRACTS-CREATE-CONTRACT': {
    integration: 'conga-contracts',
    subType: 'CONGA-CONTRACTS-CREATE-CONTRACT',
    operationName: 'Create Contract',
    operation: 'conga-contracts-create-contract',
    description: 'Create a contract in Conga Contracts',
  },
  'PUNCHOUT-GENERIC-CREATE-ORDER-REQUEST': {
    integration: 'punchout-generic',
    subType: 'PUNCHOUT-GENERIC-CREATE-ORDER-REQUEST',
    operationName: 'Create Order',
    operation: 'punchout-generic-create-order-request',
    description: 'Create a purchase order request via punchout',
  },
  'PUNCHOUT-AMAZON-BUSINESS-CREATE-ORDER-REQUEST': {
    integration: 'punchout-amazon-business',
    subType: 'PUNCHOUT-AMAZON-BUSINESS-CREATE-ORDER-REQUEST',
    operationName: 'Create Order',
    operation: 'punchout-amazon-business-create-order-request',
    description: 'Create a purchase order request via Amazon Business punchout',
  },
  'PUNCHOUT-UNITE-CREATE-ORDER-REQUEST': {
    integration: 'punchout-unite',
    subType: 'PUNCHOUT-UNITE-CREATE-ORDER-REQUEST',
    operationName: 'Create Order',
    operation: 'punchout-unite-create-order-request',
    description: 'Create a purchase order request via Unite punchout',
  },
  'PUNCHOUT-CDW-CREATE-ORDER-REQUEST': {
    integration: 'punchout-cdw',
    subType: 'PUNCHOUT-CDW-CREATE-ORDER-REQUEST',
    operationName: 'Create Order',
    operation: 'punchout-cdw-create-order-request',
    description: 'Create a purchase order request via CDW punchout',
  },
  'TROPIC-PRICE-BENCHMARK': {
    integration: 'tropic',
    subType: 'TROPIC-PRICE-BENCHMARK',
    operationName: 'Price Intelligence',
    operation: 'tropic-price-benchmark',
    description: 'Run price intelligence analysis on a supplier quote',
  },
} as const

export type ExternalOperationNode = {
  integration: GlobalIntegration
  subType: ExternalOperationSubType
  operationName: string
  operation: Operation
  description: string
}

export const getExternalOperationNodeNavigation = (nodes: ExternalOperationNode[]) => {
  return {
    name: 'External operations',
    children: nodes.map((node) => ({
      name:
        globalIntegrationsMetadata[node.integration].menuLabel ??
        `${globalIntegrationsMetadata[node.integration].title} - ${node.operationName}`,
      defaultConfig: getExternalOperationBlockConfig(node),
      type: BlockTypeEnum.EXTERNAL_OPERATION,
      subType: node.subType,
    })),
  }
}

export type CustomExternalOperationNode = {
  customIntegration: CustomIntegration
}

export const getCustomExternalOperationNodeNavigation = (customIntegrations: CustomIntegration[]) => {
  return {
    name: 'Custom integrations',
    children: customIntegrations.map((customIntegration) => ({
      name: `Custom - ${customIntegration.name}`,
      customIntegration,
      defaultConfig: getCustomExternalOperationBlockConfig(customIntegration),
      type: BlockTypeEnum.EXTERNAL_OPERATION,
      subType: 'HTTP-CUSTOM-INTEGRATION',
    })),
  }
}
export interface CreateRenewalBlockConfig extends BaseBlockConfig {
  payload: {
    label: string
    targetTimeValue: number | null
    assignees: AssigneeEntity[]
    taskDistribution?: TaskDistribution
    notificationMessage: NotificationMessage
    shouldCompleteAutomatically: boolean
    tasks: FillInFormTask[]
    workflowConfig: RenewalWorkflowConfig
    skip?: Skip
    description?: string
    escalateToManager?: boolean
  }
}

export interface RenewalWorkflowConfig {
  renewalAutoStartWorkflow: boolean
  renewalWorkflow: RenewalWorkflow
  renewalAssessmentNotice: number
  renewalInitialWorkflowAssignee: RenewalInitialWorkflowAssignee
}

export interface RenewalWorkflow {
  id: string
  title: string
}

export interface RenewalInitialWorkflowAssignee {
  type: AssigneeTypeEnum.VARIABLE
  id: string
  value: string
}

export const getBudgetApprovalNodeNavigation = (budgetApprovalGroups: { id: string; name: string }[]) => {
  const config = BlockDefaultConfig[BlockTypeEnum.BUDGET_APPROVAL]
  return {
    name: 'Budget approvals',
    children: budgetApprovalGroups.map((group) => ({
      name: group.name,
      defaultConfig: {
        ...config,
        config: {
          ...config.config,
          payload: {
            ...config.config.payload,
            label: group.name,
            budgetApprovalGroupId: group.id,
          },
        },
      },
      type: BlockTypeEnum.BUDGET_APPROVAL,
    })),
  }
}

// Notification
export interface NotificationBlockConfig extends BaseBlockConfig {
  payload: {
    assignees: AssigneeEntity[]
    taskDistribution?: TaskDistribution
    label: string
    notificationMessage: NotificationMessage
    description?: string
    externalRecipientEmails: string[] | undefined
    skip?: Skip
  }
}

// Create Renewal
export const BlockDefaultLabel: Record<BlockTypeEnum, string> = {
  [BlockTypeEnum.APPROVAL]: 'block-default-label.task',
  [BlockTypeEnum.CHILD_REQUEST_START]: 'block-default-label.child-request-start',
  [BlockTypeEnum.SUPPLIER_ONBOARDING]: 'block-default-label.supplier-portal',
  [BlockTypeEnum.MILESTONE]: 'block-default-label.milestone',
  [BlockTypeEnum.NOTIFICATION]: 'block-default-label.notify',
  [BlockTypeEnum.ENGAGEMENT]: 'block-default-label.engagement',
  [BlockTypeEnum.HTTP]: 'block-default-label.post-http',
  [BlockTypeEnum.BUDGET_APPROVAL]: 'block-default-label.budget-approval',
  [BlockTypeEnum.INTAKE]: 'block-default-label.intake',
  [BlockTypeEnum.RENEWAL_ASSESSMENT]: 'block-default-label.renewal-assessment',
  [BlockTypeEnum.EXTERNAL_OPERATION]: 'block-default-label.external-operation',
  [BlockTypeEnum.CREATE_RENEWAL]: 'block-default-label.create-renewal',
  [BlockTypeEnum.COMMIT_SUPPLIER_DATA]: 'block-default-label.commit-supplier-data',
  [BlockTypeEnum.CREATE_AGREEMENT]: 'block-default-label.create-agreement',
  [BlockTypeEnum.CANCEL_AGREEMENT]: 'block-default-label.cancel-agreement',
  [BlockTypeEnum.CANCEL_ASSESSMENTS]: 'block-default-label.cancel-assessments',
  [BlockTypeEnum.CREATE_PO]: 'block-default-label.create-po',
  [BlockTypeEnum.RFX]: 'block-default-label.rfx',
  [BlockTypeEnum.AUCTION]: 'block-default-label.auction',
  [BlockTypeEnum.CREATE_ASSESSMENT]: 'block-default-label.create-assessment',
  [BlockTypeEnum.PRICE_BENCHMARK]: 'block-default-label.price-benchmark',
  [BlockTypeEnum.DATA_TRANSFORMATION]: 'block-default-label.data-transformation',
  [BlockTypeEnum.WAITING]: 'block-default-label.waiting',
  [BlockTypeEnum.SUPPLIER_ONLINE_RESOURCES]: 'block-default-label.supplier-online-resources',
  [BlockTypeEnum.AI_FORM_COMPLETION]: 'block-default-label.ai-form-completion',
  [BlockTypeEnum.INHERENT_RISK_ASSESSMENT]: 'block-default-label.inherent-risk-assessment',
}

type DefaultConfig = {
  [BlockTypeEnum.APPROVAL]: {
    type: BlockTypeEnum.APPROVAL
    config: ApprovalBlockConfig
  }
  [BlockTypeEnum.SUPPLIER_ONBOARDING]: {
    type: BlockTypeEnum.SUPPLIER_ONBOARDING
    config: SupplierOnboardingBlockConfig
  }
  [BlockTypeEnum.MILESTONE]: {
    type: BlockTypeEnum.MILESTONE
    config: {
      payload: {
        label: string
      }
    }
  }
  [BlockTypeEnum.CHILD_REQUEST_START]: {
    type: BlockTypeEnum.CHILD_REQUEST_START
    config: LabelOnlyBlockConfig
  }
  [BlockTypeEnum.NOTIFICATION]: {
    type: BlockTypeEnum.NOTIFICATION
    config: NotificationBlockConfig
  }
  [BlockTypeEnum.ENGAGEMENT]: {
    type: BlockTypeEnum.ENGAGEMENT
    config: EngagementBlockConfig
  }
  [BlockTypeEnum.BUDGET_APPROVAL]: {
    type: BlockTypeEnum.BUDGET_APPROVAL
    config: BudgetApprovalBlockConfig
  }
  [BlockTypeEnum.HTTP]: {
    type: BlockTypeEnum.HTTP
    config: HttpBlockConfig
  }
  [BlockTypeEnum.CREATE_RENEWAL]: {
    type: BlockTypeEnum.CREATE_RENEWAL
    config: CreateRenewalBlockConfig
  }
  [BlockTypeEnum.COMMIT_SUPPLIER_DATA]: {
    type: BlockTypeEnum.COMMIT_SUPPLIER_DATA
    config: CommitSupplierDataBlockConfig
  }
  [BlockTypeEnum.CREATE_AGREEMENT]: {
    type: BlockTypeEnum.CREATE_AGREEMENT
    config: CreateAgreementBlockConfig
  }
  [BlockTypeEnum.CANCEL_AGREEMENT]: {
    type: BlockTypeEnum.CANCEL_AGREEMENT
    config: CancelAgreementBlockConfig
  }
  [BlockTypeEnum.CANCEL_ASSESSMENTS]: {
    type: BlockTypeEnum.CANCEL_ASSESSMENTS
    config: CancelAssessmentsBlockConfig
  }
  [BlockTypeEnum.CREATE_PO]: {
    type: BlockTypeEnum.CREATE_PO
    config: CreatePOBlockConfig
  }
  [BlockTypeEnum.RFX]: {
    type: BlockTypeEnum.RFX
    config: RfxBlockConfig
  }
  [BlockTypeEnum.AUCTION]: {
    type: BlockTypeEnum.AUCTION
    config: AuctionBlockConfig
  }
  [BlockTypeEnum.CREATE_ASSESSMENT]: {
    type: BlockTypeEnum.CREATE_ASSESSMENT
    config: CreateAssessmentBlockConfig
  }
  [BlockTypeEnum.PRICE_BENCHMARK]: {
    type: BlockTypeEnum.PRICE_BENCHMARK
    config: PriceBenchmarkBlockConfig
  }
  [BlockTypeEnum.DATA_TRANSFORMATION]: {
    type: BlockTypeEnum.DATA_TRANSFORMATION
    config: DataTransformationBlockConfig
  }
  [BlockTypeEnum.WAITING]: {
    type: BlockTypeEnum.WAITING
    config: WaitingBlockConfig
  }
  [BlockTypeEnum.SUPPLIER_ONLINE_RESOURCES]: {
    type: BlockTypeEnum.SUPPLIER_ONLINE_RESOURCES
    config: SupplierOnlineResourcesBlockConfig
  }
  [BlockTypeEnum.AI_FORM_COMPLETION]: {
    type: BlockTypeEnum.AI_FORM_COMPLETION
    config: AiFormCompletionBlockConfig
  }
  [BlockTypeEnum.INHERENT_RISK_ASSESSMENT]: {
    type: BlockTypeEnum.INHERENT_RISK_ASSESSMENT
    config: InherentRiskAssessmentBlockConfig
  }
}

export const BlockDefaultConfig: DefaultConfig = {
  [BlockTypeEnum.SUPPLIER_ONBOARDING]: {
    type: BlockTypeEnum.SUPPLIER_ONBOARDING,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.SUPPLIER_ONBOARDING],
        targetTimeValue: null,
        notificationMessage: defaultSupplierPortalNotificationMessage,
        tasks: [],
        prefillFromSupplierRecord: true,
        prefillFromSupplierRecordOnSupplierProductMatch: false,
        assessmentType: AssessmentType.EXTERNAL,
        shouldCompleteAutomatically: false,
      },
    },
  },
  [BlockTypeEnum.APPROVAL]: {
    type: BlockTypeEnum.APPROVAL,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.APPROVAL],
        assignees: [],
        notificationMessage: defaultTaskNotificationMessage,
        targetTimeValue: null,
        tasks: [],
        prefillFromSupplierRecord: false,
        prefillFromSupplierRecordOnSupplierProductMatch: false,
      },
    },
  },
  [BlockTypeEnum.MILESTONE]: {
    type: BlockTypeEnum.MILESTONE,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.MILESTONE],
      },
    },
  },
  [BlockTypeEnum.CHILD_REQUEST_START]: {
    type: BlockTypeEnum.CHILD_REQUEST_START,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.CHILD_REQUEST_START],
      },
    },
  },
  [BlockTypeEnum.NOTIFICATION]: {
    type: BlockTypeEnum.NOTIFICATION,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.NOTIFICATION],
        notificationMessage: defaultNotifyOnlyNotificationMessage,
        assignees: [],
        externalRecipientEmails: [],
      },
    },
  },
  [BlockTypeEnum.ENGAGEMENT]: {
    type: BlockTypeEnum.ENGAGEMENT,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.ENGAGEMENT],
        assignees: [],
        targetTimeValue: null,
        notificationMessage: defaultEngagementBlockNotificationMessage,
      },
    },
  },
  [BlockTypeEnum.BUDGET_APPROVAL]: {
    type: BlockTypeEnum.BUDGET_APPROVAL,
    config: {
      payload: {
        label: '',
        targetTimeValue: null,
        budgetApprovalGroupId: '',
        notificationMessage: defaultBudgetApprovalNotificationMessage,
        tasks: [],
        skipIfRequestor: false,
        skipIfAlreadyApproved: false,
      },
    },
  },
  [BlockTypeEnum.HTTP]: {
    type: BlockTypeEnum.HTTP,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.HTTP],
        authToken: null,
        url: '',
        headers: [],
      },
    },
  },
  [BlockTypeEnum.CREATE_RENEWAL]: {
    type: BlockTypeEnum.CREATE_RENEWAL,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.CREATE_RENEWAL],
        assignees: [],
        targetTimeValue: null,
        shouldCompleteAutomatically: false,
        notificationMessage: defaultTaskNotificationMessage,
        tasks: [],
        workflowConfig: {
          renewalAutoStartWorkflow: false,
          renewalWorkflow: {
            id: '',
            title: '',
          },
          renewalAssessmentNotice: 90,
          renewalInitialWorkflowAssignee: {
            id: '',
            type: AssigneeTypeEnum.VARIABLE,
            value: '',
          },
        },
      },
    },
  },
  [BlockTypeEnum.COMMIT_SUPPLIER_DATA]: {
    type: BlockTypeEnum.COMMIT_SUPPLIER_DATA,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.COMMIT_SUPPLIER_DATA],
        assignees: [],
        tasks: [{ type: RequestStepTaskTypeEnum.ACCOUNTING_SUPPLIER_DATA_SYNC }],
        targetTimeValue: null,
        notificationMessage: defaultCommitSupplierDataNotificationMessage,
        syncWithAccountingIntegration: true,
      },
    },
  },
  [BlockTypeEnum.CREATE_AGREEMENT]: {
    type: BlockTypeEnum.CREATE_AGREEMENT,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.CREATE_AGREEMENT],
        assignees: [],
        tasks: [],
        targetTimeValue: null,
        notificationMessage: defaultCreateAgreementNotificationMessage,
        shouldCompleteAutomatically: false,
      },
    },
  },
  [BlockTypeEnum.CANCEL_AGREEMENT]: {
    type: BlockTypeEnum.CANCEL_AGREEMENT,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.CANCEL_AGREEMENT],
        assignees: [],
        tasks: [],
        targetTimeValue: null,
        notificationMessage: defaultCancelAgreementNotificationMessage,
        shouldCompleteAutomatically: false,
      },
    },
  },
  [BlockTypeEnum.CANCEL_ASSESSMENTS]: {
    type: BlockTypeEnum.CANCEL_ASSESSMENTS,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.CANCEL_ASSESSMENTS],
        cancellationMode: CancelAssessmentsMode.All,
        targetTimeValue: null,
      },
    },
  },
  [BlockTypeEnum.CREATE_PO]: {
    type: BlockTypeEnum.CREATE_PO,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.CREATE_PO],
        assignees: [],
        tasks: [],
        targetTimeValue: null,
        notificationMessage: defaultCreatePONotificationMessage,
      },
    },
  },
  [BlockTypeEnum.RFX]: {
    type: BlockTypeEnum.RFX,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.RFX],
        assignees: [requestorAssignee],
        defaultFormTemplateIds: [],
        notificationMessage: defaultRfxNotificationMessage,
        targetTimeValue: null,
        requiresConfidentialityAgreement: false,
      },
    },
  },
  [BlockTypeEnum.AUCTION]: {
    type: BlockTypeEnum.AUCTION,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.AUCTION],
        assignees: [requestorAssignee],
        ...defaultSharedAuctionSettings,
      },
    },
  },
  [BlockTypeEnum.CREATE_ASSESSMENT]: {
    type: BlockTypeEnum.CREATE_ASSESSMENT,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.CREATE_ASSESSMENT],
        assignees: [],
        tasks: [],
        targetTimeValue: null,
        notificationMessage: defaultCreateAssessmentNotificationMessage,
        shouldCompleteAutomatically: false,
        workflowId: '',
        assessmentAssignees: [],
        frequency: '',
        shouldUseExactTargetStartDate: false,
        exactTargetStartDate: '',
        relativeTargetStartDate: '',
        autoStartEnabled: false,
      },
    },
  },
  [BlockTypeEnum.PRICE_BENCHMARK]: {
    type: BlockTypeEnum.PRICE_BENCHMARK,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.PRICE_BENCHMARK],
        assignees: [],
        targetTimeValue: null,
      },
    },
  },
  [BlockTypeEnum.DATA_TRANSFORMATION]: {
    type: BlockTypeEnum.DATA_TRANSFORMATION,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.DATA_TRANSFORMATION],
        outputs: [],
      },
    },
  },
  [BlockTypeEnum.WAITING]: {
    type: BlockTypeEnum.WAITING,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.WAITING],
        condition: {
          type: 'SINGLE' as const,
          key: 0,
          primaryField: null,
          operator: null,
          secondaryField: null,
        },
      },
    },
  },
  [BlockTypeEnum.SUPPLIER_ONLINE_RESOURCES]: {
    type: BlockTypeEnum.SUPPLIER_ONLINE_RESOURCES,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.SUPPLIER_ONLINE_RESOURCES],
        assignees: [],
        targetTimeValue: 1, // 1 hour default
        resourceTypes: [...SUPPLIER_ONLINE_RESOURCE_TYPES],
      },
    },
  },
  [BlockTypeEnum.AI_FORM_COMPLETION]: {
    type: BlockTypeEnum.AI_FORM_COMPLETION,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.AI_FORM_COMPLETION],
        assignees: [],
        targetTimeValue: null,
        tasks: [],
      },
    },
  },
  [BlockTypeEnum.INHERENT_RISK_ASSESSMENT]: {
    type: BlockTypeEnum.INHERENT_RISK_ASSESSMENT,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.INHERENT_RISK_ASSESSMENT],
        description: '',
        formSources: [],
        targetTimeValue: null,
      },
    },
  },
}

export type NodeDropData = {
  type: BlockTypeEnum
  config: BlockConfig
  validationErrors: WorkflowBlockValidationError[]
}

export const getNodeNavigation = <T extends keyof DefaultConfig>(
  type: T,
  t: TFunction
): {
  name: string
  defaultConfig: DefaultConfig[T]
  type: T
} => {
  return {
    name: t(BlockDefaultLabel[type]),
    defaultConfig: translateBlockConfig(BlockDefaultConfig[type], t),
    type,
  }
}

// HACK: Translates the label of a block config
const translateBlockConfig = <T extends keyof DefaultConfig>(config: DefaultConfig[T], t: TFunction): DefaultConfig[T] => {
  return {
    ...config,
    config: {
      ...config.config,
      payload: {
        ...config.config.payload,
        label: config.config.payload.label ? t(config.config.payload.label) : config.config.payload.label,
      },
    },
  }
}

export type NodeCustomAttributes<T> = {
  type: BlockTypeEnum
  config: T
  conditionConfig?: ConditionConfig | null
}

export type NodeMetadata = {
  type: string
  config: {
    payload: {
      label: string
      tasks?: { formTemplateId: string; type: RequestStepTaskTypeEnum }[]
      assignees?: AssigneeEntity[]
      url?: string
      description?: string
    }
  }
} & NodeCustomAttributes<BlockConfig>

export const nodeMetadataIsOfType = <T extends BlockTypeEnum>(type: T) => {
  return <M extends NodeCustomAttributes<BlockConfig>>(metadata: M): metadata is M & { type: T; config: BlockConfig<T> } => {
    return metadata.type === type
  }
}

export type NodeModel = {
  x?: number
  y?: number
  type: 'common' | 'milestone' | 'diagram-nodes'
  selected: boolean
  extras: object
  id: string
  locked: boolean
  metadata: NodeMetadata
  ports: {
    x?: number
    y?: number
    id: string
    type: string
    alignment: 'left' | 'right'
    name: string
    links: string[]
    parentNode?: string
  }[]
}

export type LinkModel = {
  type: 'common-link'
  color: string
  curvyness: number
  id: string
  labels: Array<any>
  locked: boolean
  points: Array<any>
  selected: boolean
  selectedColor: string
  source?: string
  sourcePort?: string
  target?: string
  targetPort?: string
  width: number
  extras: any
}

export type WorkflowCanvasModel = {
  offsetX: number
  offsetY: number
  zoom: number
  gridSize: number
  layers: {
    isSvg: boolean
    transformed: boolean
    models: {
      [x: string]: NodeModel | LinkModel
    }
    type: string
    selected: boolean
    extras: object
    id: string
    locked: boolean
  }[]
  id: string
  locked: boolean
}
export type CanvasModel = WorkflowCanvasModel

export type ViewModelWorkflowNode = {
  id: string
  type?: string
  data: Omit<NodeMetadata, 'type'> & { blockType: BlockTypeEnum }
}

export type ViewModelStickyNoteNode = {
  id: string
  type: 'stickyNote'
  data: { text: string }
}

export type ViewModelNode = ViewModelWorkflowNode | ViewModelStickyNoteNode

export type ViewModel = {
  nodes: Array<ViewModelNode>
  edges: Array<{
    id: string
    source: string
    target: string
  }>
  viewport: {
    x: number
    y: number
    zoom: number
  }
}

export type BlockWithAssignees =
  | ApprovalBlockConfig
  | EngagementBlockConfig
  | NotificationBlockConfig
  | RenewalAssessmentBlockConfig
  | SupplierOnboardingBlockConfig

export type EmailDeliveryError = {
  /** @description the user who did not receive the notification  */
  deliveryFailedUser: {
    id: string
    email: string
  }
  /** @description Describes whether a user has actioned a failed email notification (any request step assignee action)  */
  resolved: boolean
}
